import React, { useState } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Chip,
  Grid,
  Divider,
  List,
  ListItem,
  ListItemText,
  Paper,
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  Star as StarIcon,
  Home as HomeIcon,
  AutoAwesome as AutoAwesomeIcon,
} from '@mui/icons-material';
import { styled } from '@mui/material/styles';
import { theme, spacing, responsiveSpacing, responsiveFontSizes } from '../theme/theme';

const IntroCard = styled(Card)(({ theme }) => ({
  borderRadius: 16,
  boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
  marginBottom: theme.spacing(2),
  background: `linear-gradient(135deg, ${theme.palette.primary.main}10, ${theme.palette.secondary.main}10)`,
}));

const SectionCard = styled(Paper)(({ theme }) => ({
  borderRadius: 12,
  padding: theme.spacing(1.5),
  marginBottom: theme.spacing(1.5),
  background: 'rgba(255, 255, 255, 0.9)',
  backdropFilter: 'blur(10px)',
}));

const StarCard = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(1.5),
  borderRadius: 2,
  height: '100%',
  background: 'linear-gradient(135deg, #e8d5ff, #f3e8ff)',
  border: '1px solid rgba(147, 51, 234, 0.2)',
  transition: 'transform 0.2s ease-in-out',
  '&:hover': {
    transform: 'translateY(-2px)',
    boxShadow: '0 4px 12px rgba(147, 51, 234, 0.15)',
  },
}));

const StarChip = styled(Chip)(({ theme }) => ({
  margin: theme.spacing(0.5),
  borderRadius: 8,
  '& .MuiChip-label': {
    fontSize: '0.85rem',
    fontWeight: 500,
  },
}));

interface StarData {
  name: string;
  description: string;
}

interface PalaceData {
  chinese: string;
  english: string;
  description: string;
}

const ZiweiIntroduction: React.FC = () => {
  const [expanded, setExpanded] = useState<string | false>('overview');

  const handleChange = (panel: string) => (event: React.SyntheticEvent, isExpanded: boolean) => {
    setExpanded(isExpanded ? panel : false);
  };

  const mainStars: StarData[] = [
    { name: '紫微星', description: '紫微星是紫微斗数中的帝王星，代表权威、领导力和尊贵。' },
    { name: '天机星', description: '天机星代表智慧、机敏和变化，善于策划和思考。' },
    { name: '太阳星', description: '太阳星象征光明、热情和积极，具有照耀他人的能力。' },
    { name: '武曲星', description: '武曲星代表财富、决断和行动力，是财星之一。' },
    { name: '天同星', description: '天同星象征和谐、温和和享受，追求平稳的生活。' },
    { name: '廉贞星', description: '廉贞星代表变化、才华和桃花，性格多变。' },
    { name: '天府星', description: '天府星象征稳重、保守和储蓄，是财库星。' },
    { name: '太阴星', description: '太阴星代表温柔、内敛和感性，具有母性特质。' },
    { name: '贪狼星', description: '贪狼星象征欲望、才艺和桃花，多才多艺。' },
    { name: '巨门星', description: '巨门星代表口才、是非和暗星，善于表达。' },
    { name: '天相星', description: '天相星象征辅助、服务和协调，是很好的助手。' },
    { name: '天梁星', description: '天梁星代表长辈、庇护和化解，具有贵人运。' },
    { name: '七杀星', description: '七杀星象征冲动、勇敢和变化，具有开创精神。' },
    { name: '破军星', description: '破军星代表破坏、创新和变革，敢于突破。' },
  ];

  const auxiliaryStars: StarData[] = [
    { name: '左辅星', description: '左辅星代表辅助、帮助和贵人，能得到他人支持。' },
    { name: '右弼星', description: '右弼星象征协助、配合和人缘，善于与人合作。' },
    { name: '文昌星', description: '文昌星代表文学、才华和考试运，利于学习。' },
    { name: '文曲星', description: '文曲星象征艺术、口才和创意，具有文艺天赋。' },
    { name: '天魁星', description: '天魁星代表贵人、提拔和机会，能遇到重要人物。' },
    { name: '天钺星', description: '天钺星象征助力、支持和庇护，得到长辈帮助。' },
    { name: '禄存星', description: '禄存星代表财富、储蓄和稳定收入，是财星。' },
    { name: '天马星', description: '天马星象征变动、奔波和活跃，喜欢四处走动。' },
    { name: '擎羊星', description: '擎羊星代表冲动、竞争和刑克，性格较为急躁。' },
    { name: '陀罗星', description: '陀罗星象征阻碍、延迟和纠缠，做事容易拖延。' },
    { name: '火星', description: '火星代表急躁、冲动和意外，性格比较火爆。' },
    { name: '铃星', description: '铃星象征暗伤、慢性和潜在问题，影响较为隐蔽。' },
    { name: '地空星', description: '地空星代表空虚、理想和不切实际，容易幻想。' },
    { name: '地劫星', description: '地劫星象征破耗、损失和意外，需要小心理财。' },
  ];

  const palaces: PalaceData[] = [
    { chinese: '命宫', english: '命宫', description: '命宫代表个人的基本性格、外貌和人生态度。' },
    { chinese: '兄弟宫', english: '兄弟宫', description: '兄弟宫显示与兄弟姐妹、朋友的关系和互动。' },
    { chinese: '夫妻宫', english: '夫妻宫', description: '夫妻宫反映婚姻、感情和配偶的状况。' },
    { chinese: '子女宫', english: '子女宫', description: '子女宫显示与子女的关系、生育和教育情况。' },
    { chinese: '财帛宫', english: '财帛宫', description: '财帛宫代表财运、理财能力和金钱观念。' },
    { chinese: '疾厄宫', english: '疾厄宫', description: '疾厄宫反映健康状况、疾病和身体素质。' },
    { chinese: '迁移宫', english: '迁移宫', description: '迁移宫显示外出运、搬迁和人际关系。' },
    { chinese: '奴仆宫', english: '奴仆宫', description: '奴仆宫代表下属、朋友和社交关系。' },
    { chinese: '官禄宫', english: '官禄宫', description: '官禄宫反映事业、工作和社会地位。' },
    { chinese: '田宅宫', english: '田宅宫', description: '田宅宫显示房产、家庭和居住环境。' },
    { chinese: '福德宫', english: '福德宫', description: '福德宫代表精神享受、兴趣爱好和福气。' },
    { chinese: '父母宫', english: '父母宫', description: '父母宫反映与父母的关系、长辈和教育背景。' },
  ];

  return (
    <Box sx={{ width: '100%' }}>
      <IntroCard>
        <CardContent>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: responsiveSpacing.component }}>
            <AutoAwesomeIcon sx={{ color: theme.colors.primary, mr: responsiveSpacing.xs, fontSize: 28 }} />
            <Typography variant="h5" sx={{ fontWeight: 'bold', color: theme.colors.primary, fontSize: responsiveFontSizes.h5 }}>
              紫微斗数简介
            </Typography>
          </Box>
          <Typography variant="h6" sx={{ color: theme.colors.accent, mb: responsiveSpacing.xs, fontSize: responsiveFontSizes.h6 }}>
              中国古代占星学的瑰宝
            </Typography>
            <Typography variant="body1" sx={{ color: theme.colors.text, lineHeight: 1.6, fontSize: responsiveFontSizes.body1 }}>
              紫微斗数是中国古代占星学中最重要的分支之一，以紫微星为主星，结合其他星曜的位置和相互关系，来推断人的命运和性格特征。
            </Typography>
        </CardContent>
      </IntroCard>

      <Accordion expanded={expanded === 'overview'} onChange={handleChange('overview')}>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography variant="h6" sx={{ fontWeight: 'bold', color: theme.colors.primary, fontSize: responsiveFontSizes.h6 }}>
            📖 系统概述
          </Typography>
        </AccordionSummary>
        <AccordionDetails>
          <SectionCard>
            <Typography variant="body1" sx={{ color: theme.colors.text, lineHeight: 1.6, mb: responsiveSpacing.xs, fontSize: responsiveFontSizes.body1 }}>
              紫微斗数是一套完整的命理系统，通过分析出生时间和地点，构建个人的命盘，从而了解个人的性格、命运走向和人生机遇。
            </Typography>
            <Typography variant="body1" sx={{ color: theme.colors.text, lineHeight: 1.6, mb: responsiveSpacing.xs, fontSize: responsiveFontSizes.body1 }}>
              <strong>主要特点</strong>:
            </Typography>
            <List dense>
              <ListItem>
                <ListItemText primary="主星系统：以紫微星为首的14颗主星，决定基本性格和命运格局" />
              </ListItem>
              <ListItem>
                <ListItemText primary="辅星系统：100多颗辅助星曜，提供详细的性格特征和运势信息" />
              </ListItem>
              <ListItem>
                <ListItemText primary="十二宫位：命宫、财帛宫、事业宫等，代表人生不同领域" />
              </ListItem>
              <ListItem>
                <ListItemText primary="时间分析：大限、流年等时间周期，预测不同时期的运势变化" />
              </ListItem>
            </List>
          </SectionCard>
        </AccordionDetails>
      </Accordion>

      <Accordion expanded={expanded === 'mainStars'} onChange={handleChange('mainStars')}>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography variant="h6" sx={{ fontWeight: 'bold', color: theme.colors.primary, fontSize: responsiveFontSizes.h6 }}>
            ⭐ 主星系统
          </Typography>
        </AccordionSummary>
        <AccordionDetails>
          <SectionCard>
            <Typography variant="body1" sx={{ color: theme.colors.text, lineHeight: 1.6, mb: responsiveSpacing.xs, fontSize: responsiveFontSizes.body1 }}>
              主星是紫微斗数中最重要的星曜，共有14颗，它们决定了一个人的基本性格特征和命运格局。每颗主星都有其独特的属性和影响力。
            </Typography>
            <Grid container spacing={responsiveSpacing.xs}>
              {mainStars.map((star, index) => (
                <Grid item xs={12} sm={6} md={4} key={index}>
                  <StarCard>
                    <Typography variant="subtitle2" sx={{ fontWeight: 'bold', color: theme.colors.primary, mb: spacing.xs }}>
                      {star.name}
                    </Typography>
                    <Typography variant="body2" sx={{ color: theme.colors.text, fontSize: '0.8rem', lineHeight: 1.4 }}>
                      {star.description}
                    </Typography>
                  </StarCard>
                </Grid>
              ))}
            </Grid>
          </SectionCard>
        </AccordionDetails>
      </Accordion>

      <Accordion expanded={expanded === 'auxiliaryStars'} onChange={handleChange('auxiliaryStars')}>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography variant="h6" sx={{ fontWeight: 'bold', color: theme.colors.primary, fontSize: responsiveFontSizes.h6 }}>
            ✨ 辅助星曜
          </Typography>
        </AccordionSummary>
        <AccordionDetails>
          <SectionCard>
            <Typography variant="body1" sx={{ color: theme.colors.text, lineHeight: 1.6, mb: responsiveSpacing.xs, fontSize: responsiveFontSizes.body1 }}>
              辅助星曜是紫微斗数中的重要组成部分，它们为主星提供更详细的性格特征和运势信息，帮助更准确地分析个人命盘。
            </Typography>
            <Grid container spacing={2}>
              {auxiliaryStars.map((star, index) => (
                <Grid item xs={12} sm={6} key={index}>
                  <StarCard>
                    <Typography variant="subtitle2" sx={{ fontWeight: 'bold', color: theme.colors.accent, mb: spacing.xs }}>
                      {star.name}
                    </Typography>
                    <Typography variant="caption" sx={{ color: theme.colors.primary, mb: spacing.xs, display: 'block' }}>
                      {star.name}
                    </Typography>
                    <Typography variant="body2" sx={{ color: theme.colors.text, fontSize: '0.8rem', lineHeight: 1.4 }}>
                      {star.description}
                    </Typography>
                  </StarCard>
                </Grid>
              ))}
            </Grid>
          </SectionCard>
        </AccordionDetails>
      </Accordion>

      <Accordion expanded={expanded === 'palaces'} onChange={handleChange('palaces')}>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography variant="h6" sx={{ fontWeight: 'bold', color: theme.colors.primary, fontSize: responsiveFontSizes.h6 }}>
            🏛️ 十二宫位
          </Typography>
        </AccordionSummary>
        <AccordionDetails>
          <SectionCard>
            <Typography variant="body1" sx={{ color: theme.colors.text, lineHeight: 1.6, mb: responsiveSpacing.xs, fontSize: responsiveFontSizes.body1 }}>
              十二宫位代表人生的不同领域，每个宫位都掌管着人类经验和关系的特定方面。
            </Typography>
            <Grid container spacing={2}>
              {palaces.map((palace, index) => (
                <Grid item xs={12} sm={6} md={4} key={index}>
                  <StarCard>
                    <Typography variant="subtitle2" sx={{ fontWeight: 'bold', color: theme.colors.primary, mb: spacing.xs }}>
                      {palace.chinese}
                    </Typography>
                    <Typography variant="caption" sx={{ color: theme.colors.accent, mb: spacing.xs, display: 'block' }}>
                      {palace.english}
                    </Typography>
                    <Typography variant="body2" sx={{ color: theme.colors.text, fontSize: '0.8rem', lineHeight: 1.4 }}>
                      {palace.description}
                    </Typography>
                  </StarCard>
                </Grid>
              ))}
            </Grid>
          </SectionCard>
        </AccordionDetails>
      </Accordion>

      <Accordion expanded={expanded === 'sihua'} onChange={handleChange('sihua')}>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography variant="h6" sx={{ fontWeight: 'bold', color: theme.colors.primary, fontSize: responsiveFontSizes.h6 }}>
            🔄 四化星
          </Typography>
        </AccordionSummary>
        <AccordionDetails>
          <SectionCard>
            <Typography variant="body1" sx={{ color: theme.colors.text, lineHeight: 1.6, mb: responsiveSpacing.component, fontSize: responsiveFontSizes.body1 }}>
              四化星是紫微斗数中的重要概念，包括化禄、化权、化科、化忌四种变化，它们能够改变主星的性质，影响运势的好坏。
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6} md={3}>
                <StarCard>
                  <Typography variant="subtitle2" sx={{ fontWeight: 'bold', color: '#4ade80', mb: spacing.xs }}>
                    禄 (Lu)
                  </Typography>
                  <Typography variant="caption" sx={{ color: theme.colors.accent, mb: spacing.xs, display: 'block' }}>
                    财富繁荣
                  </Typography>
                  <Typography variant="body2" sx={{ color: theme.colors.text, fontSize: '0.8rem', lineHeight: 1.4 }}>
                    化禄代表财富、收入和物质享受，是四化中最吉利的一种，能带来好运和财富。
                  </Typography>
                </StarCard>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <StarCard>
                  <Typography variant="subtitle2" sx={{ fontWeight: 'bold', color: '#f59e0b', mb: spacing.xs }}>
                    权 (Quan)
                  </Typography>
                  <Typography variant="caption" sx={{ color: theme.colors.accent, mb: spacing.xs, display: 'block' }}>
                    权威力量
                  </Typography>
                  <Typography variant="body2" sx={{ color: theme.colors.text, fontSize: '0.8rem', lineHeight: 1.4 }}>
                    化权代表权力、地位和领导能力，能增强个人的影响力和控制力。
                  </Typography>
                </StarCard>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <StarCard>
                  <Typography variant="subtitle2" sx={{ fontWeight: 'bold', color: '#3b82f6', mb: spacing.xs }}>
                    科 (Ke)
                  </Typography>
                  <Typography variant="caption" sx={{ color: theme.colors.accent, mb: spacing.xs, display: 'block' }}>
                    学术名声
                  </Typography>
                  <Typography variant="body2" sx={{ color: theme.colors.text, fontSize: '0.8rem', lineHeight: 1.4 }}>
                    化科代表学问、名声和贵人运，有利于学习、考试和建立良好声誉。
                  </Typography>
                </StarCard>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <StarCard>
                  <Typography variant="subtitle2" sx={{ fontWeight: 'bold', color: '#ef4444', mb: spacing.xs }}>
                    忌 (Ji)
                  </Typography>
                  <Typography variant="caption" sx={{ color: theme.colors.accent, mb: spacing.xs, display: 'block' }}>
                    忌讳阻碍
                  </Typography>
                  <Typography variant="body2" sx={{ color: theme.colors.text, fontSize: '0.8rem', lineHeight: 1.4 }}>
                    化忌代表阻碍、困难和不顺，需要特别注意和化解。
                  </Typography>
                </StarCard>
              </Grid>
            </Grid>
          </SectionCard>
        </AccordionDetails>
      </Accordion>

      <Accordion expanded={expanded === 'starStrength'} onChange={handleChange('starStrength')}>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography variant="h6" sx={{ fontWeight: 'bold', color: theme.colors.primary, fontSize: responsiveFontSizes.h6 }}>
            💪 星情强弱
          </Typography>
        </AccordionSummary>
        <AccordionDetails>
          <SectionCard>
            <Typography variant="body1" sx={{ color: theme.colors.text, lineHeight: 1.6, mb: responsiveSpacing.xs, fontSize: responsiveFontSizes.body1 }}>
              星情强弱是指星曜在不同宫位的力量表现，分为庙、旺、得地、平、陷五个等级，影响星曜发挥作用的程度。
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6} md={2.4}>
                <StarCard>
                  <Typography variant="subtitle2" sx={{ fontWeight: 'bold', color: '#10b981', mb: responsiveSpacing.xs, fontSize: responsiveFontSizes.body2 }}>
                      庙 (Miao)
                    </Typography>
                    <Typography variant="caption" sx={{ color: theme.colors.accent, mb: responsiveSpacing.xs, display: 'block', fontSize: responsiveFontSizes.body2 }}>
                      庙旺得位
                    </Typography>
                    <Typography variant="body2" sx={{ color: theme.colors.text, fontSize: responsiveFontSizes.body2, lineHeight: 1.4 }}>
                    星曜力量最强，能充分发挥正面作用，是最理想的状态。
                  </Typography>
                </StarCard>
              </Grid>
              <Grid item xs={12} sm={6} md={2.4}>
                <StarCard>
                  <Typography variant="subtitle2" sx={{ fontWeight: 'bold', color: '#06b6d4', mb: responsiveSpacing.xs, fontSize: responsiveFontSizes.body2 }}>
                      旺 (Wang)
                    </Typography>
                    <Typography variant="caption" sx={{ color: theme.colors.accent, mb: responsiveSpacing.xs, display: 'block', fontSize: responsiveFontSizes.body2 }}>
                      繁荣强盛
                    </Typography>
                    <Typography variant="body2" sx={{ color: theme.colors.text, fontSize: responsiveFontSizes.body2, lineHeight: 1.4 }}>
                    星曜力量很强，能发挥良好的正面影响。
                  </Typography>
                </StarCard>
              </Grid>
              <Grid item xs={12} sm={6} md={2.4}>
                <StarCard>
                  <Typography variant="subtitle2" sx={{ fontWeight: 'bold', color: '#8b5cf6', mb: responsiveSpacing.xs, fontSize: responsiveFontSizes.body2 }}>
                      得地 (De Di)
                    </Typography>
                    <Typography variant="caption" sx={{ color: theme.colors.accent, mb: responsiveSpacing.xs, display: 'block', fontSize: responsiveFontSizes.body2 }}>
                      得地有利
                    </Typography>
                    <Typography variant="body2" sx={{ color: theme.colors.text, fontSize: responsiveFontSizes.body2, lineHeight: 1.4 }}>
                    星曜力量中等偏上，有一定的正面作用。
                  </Typography>
                </StarCard>
              </Grid>
              <Grid item xs={12} sm={6} md={2.4}>
                <StarCard>
                  <Typography variant="subtitle2" sx={{ fontWeight: 'bold', color: '#f59e0b', mb: responsiveSpacing.xs, fontSize: responsiveFontSizes.body2 }}>
                      平 (Ping)
                    </Typography>
                    <Typography variant="caption" sx={{ color: theme.colors.accent, mb: responsiveSpacing.xs, display: 'block', fontSize: responsiveFontSizes.body2 }}>
                      中性平常
                    </Typography>
                    <Typography variant="body2" sx={{ color: theme.colors.text, fontSize: responsiveFontSizes.body2, lineHeight: 1.4 }}>
                    星曜力量平常，作用不明显，需要其他因素配合。
                  </Typography>
                </StarCard>
              </Grid>
              <Grid item xs={12} sm={6} md={2.4}>
                <StarCard>
                  <Typography variant="subtitle2" sx={{ fontWeight: 'bold', color: '#dc2626', mb: responsiveSpacing.xs, fontSize: responsiveFontSizes.body2 }}>
                      陷 (Xian)
                    </Typography>
                    <Typography variant="caption" sx={{ color: theme.colors.accent, mb: responsiveSpacing.xs, display: 'block', fontSize: responsiveFontSizes.body2 }}>
                      陷落衰弱
                    </Typography>
                    <Typography variant="body2" sx={{ color: theme.colors.text, fontSize: responsiveFontSizes.body2, lineHeight: 1.4 }}>
                    星曜力量最弱，容易产生负面影响，需要特别注意。
                  </Typography>
                </StarCard>
              </Grid>
            </Grid>
          </SectionCard>
        </AccordionDetails>
      </Accordion>

      <Accordion expanded={expanded === 'triangulation'} onChange={handleChange('triangulation')}>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography variant="h6" sx={{ fontWeight: 'bold', color: theme.colors.primary, fontSize: responsiveFontSizes.h6 }}>
            🔺 三方四正看盘法
          </Typography>
        </AccordionSummary>
        <AccordionDetails>
          <SectionCard>
            <Typography variant="body1" sx={{ color: theme.colors.text, lineHeight: 1.6, mb: responsiveSpacing.component, fontSize: responsiveFontSizes.body1 }}>
              三方四正是紫微斗数中重要的分析方法，通过观察某宫位及其相关联的宫位，来全面了解该领域的运势。
            </Typography>
            <Typography variant="body1" sx={{ color: theme.colors.text, lineHeight: 1.6, mb: responsiveSpacing.component, fontSize: responsiveFontSizes.body1 }}>
              <strong>三方</strong>: 指本宫、对宫、财帛宫三个宫位，形成三角关系。
            </Typography>
            <Typography variant="body1" sx={{ color: theme.colors.text, lineHeight: 1.6, mb: responsiveSpacing.component, fontSize: responsiveFontSizes.body1 }}>
              <strong>四正</strong>: 在三方基础上加上迁移宫，形成四个正位。
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <StarCard>
                  <Typography variant="subtitle2" sx={{ fontWeight: 'bold', color: theme.colors.primary, mb: spacing.xs }}>
                    分析优势
                  </Typography>
                  <List dense>
                    <ListItem sx={{ py: 0 }}>
                      <ListItemText 
                        primary="• 全面的人生视角" 
                        primaryTypographyProps={{ fontSize: '0.8rem' }}
                      />
                    </ListItem>
                    <ListItem sx={{ py: 0 }}>
                      <ListItemText 
                        primary="• 理解星曜间的相互作用" 
                        primaryTypographyProps={{ fontSize: '0.8rem' }}
                      />
                    </ListItem>
                    <ListItem sx={{ py: 0 }}>
                      <ListItemText 
                        primary="• 平衡的解读方法" 
                        primaryTypographyProps={{ fontSize: '0.8rem' }}
                      />
                    </ListItem>
                    <ListItem sx={{ py: 0 }}>
                      <ListItemText 
                        primary="• 识别优势和弱点" 
                        primaryTypographyProps={{ fontSize: '0.8rem' }}
                      />
                    </ListItem>
                  </List>
                </StarCard>
              </Grid>
              <Grid item xs={12} md={6}>
                <StarCard>
                  <Typography variant="subtitle2" sx={{ fontWeight: 'bold', color: theme.colors.accent, mb: spacing.xs }}>
                    实际应用
                  </Typography>
                  <List dense>
                    <ListItem sx={{ py: 0 }}>
                      <ListItemText 
                        primary="• 事业分析" 
                        primaryTypographyProps={{ fontSize: '0.8rem' }}
                      />
                    </ListItem>
                    <ListItem sx={{ py: 0 }}>
                      <ListItemText 
                        primary="• 感情分析" 
                        primaryTypographyProps={{ fontSize: '0.8rem' }}
                      />
                    </ListItem>
                    <ListItem sx={{ py: 0 }}>
                      <ListItemText 
                        primary="• 健康分析" 
                        primaryTypographyProps={{ fontSize: '0.8rem' }}
                      />
                    </ListItem>
                  </List>
                </StarCard>
              </Grid>
            </Grid>
          </SectionCard>
        </AccordionDetails>
      </Accordion>

      <Accordion expanded={expanded === 'majorPeriods'} onChange={handleChange('majorPeriods')}>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography variant="h6" sx={{ fontWeight: 'bold', color: theme.colors.primary, fontSize: responsiveFontSizes.h6 }}>
            📅 大限
          </Typography>
        </AccordionSummary>
        <AccordionDetails>
          <SectionCard>
            <Typography variant="body1" sx={{ color: theme.colors.text, lineHeight: 1.6, mb: responsiveSpacing.component, fontSize: responsiveFontSizes.body1 }}>
              大限是紫微斗数中的重要概念，代表人生中每十年的运势变化。通过分析大限，可以了解不同人生阶段的机遇与挑战。
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <StarCard>
                  <Typography variant="subtitle2" sx={{ fontWeight: 'bold', color: theme.colors.primary, mb: spacing.xs }}>
                    大限分析
                  </Typography>
                  <List dense>
                    <ListItem sx={{ py: 0 }}>
                      <ListItemText 
                        primary="• 每个大限持续十年" 
                        primaryTypographyProps={{ fontSize: '0.8rem' }}
                      />
                    </ListItem>
                    <ListItem sx={{ py: 0 }}>
                      <ListItemText 
                        primary="• 人生阶段分析" 
                        primaryTypographyProps={{ fontSize: '0.8rem' }}
                      />
                    </ListItem>
                    <ListItem sx={{ py: 0 }}>
                      <ListItemText 
                        primary="• 运势变化趋势" 
                        primaryTypographyProps={{ fontSize: '0.8rem' }}
                      />
                    </ListItem>
                    <ListItem sx={{ py: 0 }}>
                      <ListItemText 
                        primary="• 分析应用" 
                        primaryTypographyProps={{ fontSize: '0.8rem' }}
                      />
                    </ListItem>
                  </List>
                </StarCard>
              </Grid>
              <Grid item xs={12} md={6}>
                <StarCard>
                  <Typography variant="subtitle2" sx={{ fontWeight: 'bold', color: theme.colors.accent, mb: spacing.xs }}>
                    分析应用
                  </Typography>
                  <List dense>
                    <ListItem sx={{ py: 0 }}>
                      <ListItemText 
                        primary="• 事业发展时机" 
                        primaryTypographyProps={{ fontSize: '0.8rem' }}
                      />
                    </ListItem>
                    <ListItem sx={{ py: 0 }}>
                      <ListItemText 
                        primary="• 感情婚姻时机" 
                        primaryTypographyProps={{ fontSize: '0.8rem' }}
                      />
                    </ListItem>
                    <ListItem sx={{ py: 0 }}>
                      <ListItemText 
                        primary="• 财富积累期" 
                        primaryTypographyProps={{ fontSize: '0.8rem' }}
                      />
                    </ListItem>
                    <ListItem sx={{ py: 0 }}>
                      <ListItemText 
                        primary="• 健康生活变化" 
                        primaryTypographyProps={{ fontSize: '0.8rem' }}
                      />
                    </ListItem>
                  </List>
                </StarCard>
              </Grid>
            </Grid>
          </SectionCard>
        </AccordionDetails>
      </Accordion>

      <Accordion expanded={expanded === 'annualFortune'} onChange={handleChange('annualFortune')}>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography variant="h6" sx={{ fontWeight: 'bold', color: theme.colors.primary, fontSize: responsiveFontSizes.h6 }}>
            🗓️ 流年
          </Typography>
        </AccordionSummary>
        <AccordionDetails>
          <SectionCard>
            <Typography variant="body1" sx={{ color: theme.colors.text, lineHeight: 1.6, mb: responsiveSpacing.component, fontSize: responsiveFontSizes.body1 }}>
              流年是紫微斗数中分析每年运势的重要方法，通过观察流年星曜的变化，可以预测一年中的吉凶祸福和重要事件。
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} md={4}>
                <StarCard>
                  <Typography variant="subtitle2" sx={{ fontWeight: 'bold', color: theme.colors.primary, mb: spacing.xs }}>
                    流年分析
                  </Typography>
                  <List dense>
                    <ListItem sx={{ py: 0 }}>
                      <ListItemText 
                        primary="• 年度运势趋势" 
                        primaryTypographyProps={{ fontSize: '0.8rem' }}
                      />
                    </ListItem>
                    <ListItem sx={{ py: 0 }}>
                      <ListItemText 
                        primary="• 月度变化" 
                        primaryTypographyProps={{ fontSize: '0.8rem' }}
                      />
                    </ListItem>
                    <ListItem sx={{ py: 0 }}>
                      <ListItemText 
                        primary="• 特定事件时机" 
                        primaryTypographyProps={{ fontSize: '0.8rem' }}
                      />
                    </ListItem>
                    <ListItem sx={{ py: 0 }}>
                      <ListItemText 
                        primary="• 机会窗口" 
                        primaryTypographyProps={{ fontSize: '0.8rem' }}
                      />
                    </ListItem>
                  </List>
                </StarCard>
              </Grid>
              <Grid item xs={12} md={4}>
                <StarCard>
                  <Typography variant="subtitle2" sx={{ fontWeight: 'bold', color: theme.colors.accent, mb: spacing.xs }}>
                    关键因素
                  </Typography>
                  <List dense>
                    <ListItem sx={{ py: 0 }}>
                      <ListItemText 
                        primary="• 飞星位置" 
                        primaryTypographyProps={{ fontSize: '0.8rem' }}
                      />
                    </ListItem>
                    <ListItem sx={{ py: 0 }}>
                      <ListItemText 
                        primary="• 四化星" 
                        primaryTypographyProps={{ fontSize: '0.8rem' }}
                      />
                    </ListItem>
                    <ListItem sx={{ py: 0 }}>
                      <ListItemText 
                        primary="• 宫位相互作用" 
                        primaryTypographyProps={{ fontSize: '0.8rem' }}
                      />
                    </ListItem>
                    <ListItem sx={{ py: 0 }}>
                      <ListItemText 
                        primary="• 冲合格局" 
                        primaryTypographyProps={{ fontSize: '0.8rem' }}
                      />
                    </ListItem>
                  </List>
                </StarCard>
              </Grid>
              <Grid item xs={12} md={4}>
                <StarCard>
                  <Typography variant="subtitle2" sx={{ fontWeight: 'bold', color: '#8b5cf6', mb: spacing.xs }}>
                    实际用途
                  </Typography>
                  <List dense>
                    <ListItem sx={{ py: 0 }}>
                      <ListItemText 
                        primary="• 投资时机" 
                        primaryTypographyProps={{ fontSize: '0.8rem' }}
                      />
                    </ListItem>
                    <ListItem sx={{ py: 0 }}>
                      <ListItemText 
                        primary="• 旅行规划" 
                        primaryTypographyProps={{ fontSize: '0.8rem' }}
                      />
                    </ListItem>
                    <ListItem sx={{ py: 0 }}>
                      <ListItemText 
                        primary="• 重要决策" 
                        primaryTypographyProps={{ fontSize: '0.8rem' }}
                      />
                    </ListItem>
                    <ListItem sx={{ py: 0 }}>
                      <ListItemText 
                        primary="• 健康预防" 
                        primaryTypographyProps={{ fontSize: '0.8rem' }}
                      />
                    </ListItem>
                  </List>
                </StarCard>
              </Grid>
            </Grid>
          </SectionCard>
        </AccordionDetails>
      </Accordion>

      <IntroCard sx={{ mt: responsiveSpacing.section }}>
        <CardContent>
          <Typography variant="h6" sx={{ fontWeight: 'bold', color: theme.colors.primary, mb: responsiveSpacing.component, fontSize: responsiveFontSizes.h6 }}>
            🔮 开始使用
          </Typography>
          <Typography variant="body1" sx={{ color: theme.colors.text, lineHeight: 1.6, mb: responsiveSpacing.component, fontSize: responsiveFontSizes.body1 }}>
            紫微斗数可以为您提供深入的人生洞察和指导。通过分析您的命盘，我们可以帮助您了解：
          </Typography>
          <Box component="ul" sx={{ color: theme.colors.text, pl: responsiveSpacing.component, mb: responsiveSpacing.component, fontSize: responsiveFontSizes.body1 }}>
            <li>个性特征分析</li>
            <li>事业发展指导</li>
            <li>感情关系指导</li>
            <li>健康建议</li>
            <li>决策时机把握</li>
          </Box>
          <Typography variant="body1" sx={{ color: theme.colors.text, lineHeight: 1.6, fontSize: responsiveFontSizes.body1 }}>
            开始您的紫微斗数之旅，探索命运的奥秘，掌握人生的主动权。
          </Typography>
        </CardContent>
      </IntroCard>
    </Box>
  );
};

export default ZiweiIntroduction;